/**
 * Shared renderer for a Kim message's interleaved text/reference/artifact content
 * blocks, used by both the frontend (format-content.ts, placeholder tags) and the
 * backend Word export (kim-export-content.utils.ts, [n] markers). Streaming splits
 * table rows across blocks, so the two sides must agree on the repairs that keep
 * the table's row structure intact — and thereby produce the same layout.
 */
import type {
  ArtifactBlock,
  ArtifactContent,
  ContentBlock,
  ReferenceBlock,
} from '../types/types.js';

export interface ContentBlockRenderers {
  text: (value: string) => string;
  reference: (block: ReferenceBlock) => string;
  artifact: (block: ArtifactBlock) => string;
}

export function renderContentBlocks(
  blocks: (ContentBlock | ArtifactContent)[],
  render: ContentBlockRenderers,
): string {
  let result = '';
  // The pure markdown emitted so far (text blocks only, no reference/artifact output). Used to
  // decide whether a reference sits after a finished table row, independent of how streaming
  // chunked it.
  let markdown = '';
  let refsBreakOut = false;
  let prevWasRef = false;
  let prevWasText = false;

  for (const block of blocks) {
    if (block.type === 'text') {
      const value = render.text(block.value);
      // Only break before a row when a reference/artifact interrupted the markdown. Two
      // consecutive text blocks are one continuous model stream whose own newlines already
      // separate rows, so a leading '|' there is a mid-row cell separator, not a new row.
      // The repaired newline must land in `markdown` too, otherwise consecutive rows glue
      // into one overlong line and the next reference wrongly reads the row as closed.
      const repaired = startsNewTableRow(value, prevWasText, result) ? `\n${value}` : value;
      result += repaired;
      markdown += repaired;
      prevWasRef = false;
      prevWasText = true;
    } else if (block.type === 'artifact') {
      result += render.artifact(block);
      markdown = '';
      refsBreakOut = false;
      prevWasRef = false;
      prevWasText = false;
    } else if (block.type === 'reference') {
      // References that follow a full table row (no empty cell to fill) belong after the table,
      // not in it.
      if (!prevWasRef) refsBreakOut = endsOnClosedTableRow(markdown);
      if (refsBreakOut && !prevWasRef) {
        result += '\n\n';
        // The table is finished; later references must judge themselves against fresh content.
        markdown = '';
      }
      result += render.reference(block);
      prevWasRef = true;
      prevWasText = false;
    }
    // Survey/inconsistency blocks are not rendered inline.
  }
  return result;
}

const TABLE_SEPARATOR = /^\s*\|?[\s:|-]+\|?\s*$/;

function countTableCells(line: string): number {
  const trimmed = line.trim();
  if (!trimmed.startsWith('|')) return 0;
  const cells = trimmed.split('|').slice(1);
  if (trimmed.endsWith('|')) cells.pop();
  return cells.length;
}

// True when the accumulated markdown ends on a complete, closed table row (every column filled).
// A reference at that position has no cell to occupy, so it must not be appended after the
// row's closing '|'. Derived from the full text — not a single block — because streaming splits
// one table across many text blocks, so the final block alone is an unreliable fragment.
function endsOnClosedTableRow(markdown: string): boolean {
  const lines = markdown.split('\n').filter((line) => line.trim().length > 0);
  if (lines.length === 0) return false;

  let columns: number | null = null;
  for (const line of lines) {
    if (line.includes('-') && TABLE_SEPARATOR.test(line)) columns = countTableCells(line);
  }
  if (columns === null) return false;

  const lastLine = lines[lines.length - 1].trim();
  return lastLine.startsWith('|') && lastLine.endsWith('|') && countTableCells(lastLine) >= columns;
}

// True when a text block that follows a non-text block (reference/artifact placeholder) opens a
// new table row. Two consecutive text blocks are one continuous model stream whose own newlines
// already separate rows, so a leading '|' there is a mid-row cell separator, not a new row.
function startsNewTableRow(value: string, prevWasText: boolean, soFar: string): boolean {
  return !prevWasText && value.startsWith('|') && soFar.length > 0 && !soFar.endsWith('\n');
}
